import { useState, useEffect, useCallback } from "react";
import { User } from "oidc-client-ts";
import oidcService, { userManager, LoginOptions } from "../oidc";

export interface AppUser {
  id: string;
  username: string;
  email: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  profile_picture_path?: string;
  is_active: boolean;
  is_admin: boolean;
  permissions: string[];
  organisation_id?: string;
  created_at: string;
  last_login?: string;
}

interface UseAuthenticationReturn {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  login: (options?: LoginOptions) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  updateUser: (userData: Partial<AppUser>) => void;
  checkAuthStatus: () => Promise<{
    isAuthenticated: boolean;
    isLoading: boolean;
  }>;
}

export const useAuthentication = (): UseAuthenticationReturn => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const convertOidcUserToAppUser = useCallback(
    async (oidcUser: User): Promise<AppUser> => {
      const profile = oidcUser.profile;
      const permissions = await oidcService.getPermissions();

      return {
        id: profile.sub || "",
        username:
          profile.preferred_username || profile.name || profile.email || "",
        email: profile.email || "",
        display_name: profile.name || "",
        first_name: profile.given_name || "",
        last_name: profile.family_name || "",
        profile_picture_path: profile.picture || "",
        is_active: true,
        is_admin: false,
        permissions,
        organisation_id: (profile as Record<string, unknown>).org_id as
          | string
          | undefined,
        created_at: new Date().toISOString(),
        last_login: new Date().toISOString(),
      };
    },
    [],
  );

  const login = useCallback(async (options?: LoginOptions): Promise<void> => {
    try {
      setIsLoading(true);
      setSessionExpired(false);
      await oidcService.login(options);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Wird von client.ts (außerhalb des React-Baums über `window.__AUTH_PROVIDER__`)
   * aufgerufen, wenn ein aktiver Token-Refresh nach einem 401 fehlgeschlagen ist.
   * Löst zentral, unabhängig von der auslösenden Domäne, das blockierende
   * "Sitzung abgelaufen"-Modal aus.
   */
  const notifySessionExpired = useCallback(() => {
    setSessionExpired(true);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await oidcService.logout();
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const isOidcAuthenticated = await oidcService.isAuthenticated();
      if (!isOidcAuthenticated) return null;
      return await oidcService.getAccessToken();
    } catch {
      return null;
    }
  }, []);

  const updateUser = useCallback(
    (userData: Partial<AppUser>) => {
      if (user) {
        setUser({ ...user, ...userData });
      }
    },
    [user],
  );

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        const oidcUser = await oidcService.getUser();

        if (oidcUser && !oidcUser.expired) {
          const appUser = await convertOidcUserToAppUser(oidcUser);
          setUser(appUser);
        } else {
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [convertOidcUserToAppUser]);

  useEffect(() => {
    const handleUserLoaded = async (oidcUser: User) => {
      try {
        const appUser = await convertOidcUserToAppUser(oidcUser);
        setUser(appUser);
        setSessionExpired(false);
      } catch {
        // Fehler beim Konvertieren ignorieren
      }
    };

    const handleUserUnloaded = () => {
      setUser(null);
      setIsLoading(false);
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);

    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
    };
  }, [convertOidcUserToAppUser]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).__AUTH_PROVIDER__ = {
        getAccessToken,
        notifySessionExpired,
        user,
        isAuthenticated: !!user,
      };
    }

    return () => {
      if (typeof window !== "undefined") {
        delete (window as any).__AUTH_PROVIDER__;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).__AUTH_PROVIDER__) {
      (window as any).__AUTH_PROVIDER__.user = user;
      (window as any).__AUTH_PROVIDER__.isAuthenticated = !!user;
    }
  }, [user]);

  const checkAuthStatus = useCallback(async (): Promise<{
    isAuthenticated: boolean;
    isLoading: boolean;
  }> => {
    if (!isLoading) {
      return { isAuthenticated: !!user, isLoading: false };
    }
    return { isAuthenticated: false, isLoading: true };
  }, [user, isLoading]);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    sessionExpired,
    login,
    logout,
    getAccessToken,
    updateUser,
    checkAuthStatus,
  };
};
